import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  UsersIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';

const PLAN_OPTION_CHOICES = [2, 3, 4, 6, 9, 12];

const emptyProgramForm = {
  name: '',
  description: '',
  start_date: '',
  end_date: '',
  season: '',
  preseason_start_date: '',
  regular_season_start_date: '',
  regular_season_end_date: '',
  postseason_start_date: '',
  postseason_format: 'none',
  round_robin_times_through: 1
};

const POSTSEASON_FORMATS = [
  { value: 'none', label: 'No postseason' },
  { value: 'single_elimination', label: 'Single elimination' },
  { value: 'double_elimination', label: 'Double elimination (4/8/16/32 teams)' }
];

const emptyFeeForm = {
  title: '',
  description: '',
  amount: '',
  age_group_id: '',
  due_date: '',
  program_end_date: '',
  auto_invoice_on_assignment: true,
  payment_plan_enabled: false,
  payment_plan_options: []
};

const Programs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [programs, setPrograms] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showProgramForm, setShowProgramForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState(emptyProgramForm);

  // Per-program expanded detail (fees + tryouts + teams)
  const [expandedProgramId, setExpandedProgramId] = useState(null);
  const [programDetail, setProgramDetail] = useState(null); // { fees, tryouts, teams }
  const [feeForm, setFeeForm] = useState(emptyFeeForm);
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [orgTeams, setOrgTeams] = useState([]);       // all active teams for the selected org
  const [showTeamPicker, setShowTeamPicker] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get('/api/organizations/my-organizations', { headers: authHeaders });
      const orgs = Array.isArray(response.data) ? response.data : [];
      const transformed = orgs.map(orgUser => (orgUser.Organization
        ? { id: orgUser.Organization.id, name: orgUser.Organization.name }
        : orgUser));
      setOrganizations(transformed);
      if (transformed.length > 0 && !selectedOrg) {
        setSelectedOrg(transformed[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchPrograms = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/programs?organization_id=${selectedOrg}`, { headers: authHeaders });
      setPrograms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg]);

  const fetchAgeGroups = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      const response = await axios.get(`/api/age-groups?organizationId=${selectedOrg}`, { headers: authHeaders });
      setAgeGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching age groups:', error);
      setAgeGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg]);

  useEffect(() => {
    fetchPrograms();
    fetchAgeGroups();
    setExpandedProgramId(null);
    setProgramDetail(null);
  }, [fetchPrograms, fetchAgeGroups]);

  const openCreateProgram = () => {
    setEditingProgram(null);
    setProgramForm(emptyProgramForm);
    setShowProgramForm(true);
  };

  const openEditProgram = (program) => {
    setEditingProgram(program);
    setProgramForm({
      name: program.name || '',
      description: program.description || '',
      start_date: program.start_date || '',
      end_date: program.end_date || '',
      season: program.season || '',
      preseason_start_date: program.preseason_start_date || '',
      regular_season_start_date: program.regular_season_start_date || '',
      regular_season_end_date: program.regular_season_end_date || '',
      postseason_start_date: program.postseason_start_date || '',
      postseason_format: program.postseason_format || 'none',
      round_robin_times_through: program.round_robin_times_through || 1
    });
    setShowProgramForm(true);
  };

  const handleProgramFieldChange = (e) => {
    const { name, value } = e.target;
    setProgramForm(prev => ({ ...prev, [name]: value }));
  };

  const submitProgram = async (e) => {
    e.preventDefault();
    if (!programForm.name.trim() || !programForm.start_date) {
      toast.error('Program name and start date are required.');
      return;
    }
    try {
      const payload = {
        name: programForm.name.trim(),
        description: programForm.description.trim() || null,
        start_date: programForm.start_date,
        end_date: programForm.end_date || null,
        season: programForm.season.trim() || null,
        preseason_start_date: programForm.preseason_start_date || null,
        regular_season_start_date: programForm.regular_season_start_date || null,
        regular_season_end_date: programForm.regular_season_end_date || null,
        postseason_start_date: programForm.postseason_start_date || null,
        postseason_format: programForm.postseason_format || 'none',
        round_robin_times_through: parseInt(programForm.round_robin_times_through, 10) || 1
      };
      if (editingProgram) {
        await axios.put(`/api/programs/${editingProgram.id}`, payload, { headers: authHeaders });
        toast.success('Program updated');
      } else {
        await axios.post('/api/programs', { ...payload, organization_id: parseInt(selectedOrg, 10) }, { headers: authHeaders });
        toast.success('Program created');
      }
      setShowProgramForm(false);
      setEditingProgram(null);
      fetchPrograms();
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error(error.response?.data?.message || 'Failed to save program');
    }
  };

  const archiveProgram = async (program) => {
    if (!window.confirm(`Archive program "${program.name}"? Existing tryouts and invoices are not affected.`)) return;
    try {
      await axios.delete(`/api/programs/${program.id}`, { headers: authHeaders });
      toast.success('Program archived');
      fetchPrograms();
    } catch (error) {
      console.error('Error archiving program:', error);
      toast.error(error.response?.data?.message || 'Failed to archive program');
    }
  };

  const toggleExpand = async (program) => {
    if (expandedProgramId === program.id) {
      setExpandedProgramId(null);
      setProgramDetail(null);
      setShowFeeForm(false);
      return;
    }
    setExpandedProgramId(program.id);
    setShowFeeForm(false);
    await loadProgramDetail(program.id);
  };

  const loadProgramDetail = async (programId) => {
    try {
      const [detailResp, feesResp] = await Promise.all([
        axios.get(`/api/programs/${programId}`, { headers: authHeaders }),
        axios.get(`/api/program-fees?program_id=${programId}&active=true`, { headers: authHeaders })
      ]);
      setProgramDetail({
        program: detailResp.data,
        tryouts: detailResp.data?.Tryouts || [],
        teams: detailResp.data?.Teams || [],
        fees: Array.isArray(feesResp.data) ? feesResp.data : []
      });
    } catch (error) {
      console.error('Error loading program detail:', error);
      toast.error('Failed to load program detail');
    }
  };

  const loadOrgTeams = async () => {
    if (!selectedOrg) return;
    try {
      const resp = await axios.get(`/api/teams?organizationId=${selectedOrg}`, { headers: authHeaders });
      setOrgTeams(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.error('Error loading org teams:', e);
    }
  };

  const addTeamToProgram = async (programId, teamId) => {
    try {
      await axios.post(`/api/programs/${programId}/teams`, { teamId }, { headers: authHeaders });
      toast.success('Team added to program');
      setShowTeamPicker(false);
      await loadProgramDetail(programId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add team');
    }
  };

  const removeTeamFromProgram = async (programId, teamId) => {
    if (!window.confirm('Remove this team from the program?')) return;
    try {
      await axios.delete(`/api/programs/${programId}/teams/${teamId}`, { headers: authHeaders });
      toast.success('Team removed from program');
      await loadProgramDetail(programId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove team');
    }
  };

  const handleFeeFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFeeForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const togglePlanOption = (months) => {
    setFeeForm(prev => {
      const set = new Set(prev.payment_plan_options);
      if (set.has(months)) set.delete(months); else set.add(months);
      return { ...prev, payment_plan_options: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const submitFee = async (e, programId) => {
    e.preventDefault();
    if (!feeForm.title.trim() || feeForm.amount === '' || isNaN(parseFloat(feeForm.amount))) {
      toast.error('Fee title and amount are required.');
      return;
    }
    try {
      const payload = {
        program_id: programId,
        title: feeForm.title.trim(),
        description: feeForm.description.trim() || null,
        amount: parseFloat(feeForm.amount),
        age_group_id: feeForm.age_group_id ? parseInt(feeForm.age_group_id, 10) : null,
        due_date: feeForm.due_date || null,
        program_end_date: feeForm.program_end_date || null,
        auto_invoice_on_assignment: feeForm.auto_invoice_on_assignment !== false,
        payment_plan_enabled: !!feeForm.payment_plan_enabled,
        payment_plan_options: feeForm.payment_plan_enabled ? feeForm.payment_plan_options : []
      };
      await axios.post('/api/program-fees', payload, { headers: authHeaders });
      toast.success('Program fee added');
      setFeeForm(emptyFeeForm);
      setShowFeeForm(false);
      await loadProgramDetail(programId);
    } catch (error) {
      console.error('Error adding program fee:', error);
      toast.error(error.response?.data?.message || 'Failed to add program fee');
    }
  };

  const archiveFee = async (feeId, programId) => {
    if (!window.confirm('Archive this program fee?')) return;
    try {
      await axios.delete(`/api/program-fees/${feeId}`, { headers: authHeaders });
      toast.success('Fee archived');
      await loadProgramDetail(programId);
    } catch (error) {
      console.error('Error archiving fee:', error);
      toast.error(error.response?.data?.message || 'Failed to archive fee');
    }
  };

  const ageGroupName = (id) => ageGroups.find(ag => ag.id === id)?.name || null;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <RectangleStackIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Programs</h1>
            <p className="text-sm text-gray-500">Manage seasons, program fees, tryouts, and player assignment.</p>
          </div>
        </div>
        <button
          onClick={openCreateProgram}
          disabled={!selectedOrg}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          <PlusIcon className="h-5 w-5" /> New Program
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select an organization</option>
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading programs...</div>
      ) : programs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          No programs yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {programs.map(program => (
            <div key={program.id} className={`bg-white rounded-lg shadow ${program.is_active ? '' : 'opacity-60'}`}>
              <div className="p-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{program.name}</h3>
                    {program.season && (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{program.season}</span>
                    )}
                    {!program.is_active && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Archived</span>
                    )}
                  </div>
                  {program.description && <p className="text-sm text-gray-600 mt-1">{program.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDaysIcon className="h-4 w-4" />
                      {program.start_date}{program.end_date ? ` - ${program.end_date}` : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => navigate(`/program-assignment?programId=${program.id}`)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded hover:bg-primary-100"
                  >
                    <UsersIcon className="h-4 w-4" /> Assign / Roll Forward
                  </button>
                  <button onClick={() => openEditProgram(program)} className="p-2 text-gray-500 hover:text-gray-700" title="Edit">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  {program.is_active && (
                    <button onClick={() => archiveProgram(program)} className="p-2 text-gray-500 hover:text-red-600" title="Archive">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 px-4 py-2">
                <button
                  onClick={() => toggleExpand(program)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {expandedProgramId === program.id ? 'Hide details' : 'Manage fees & tryouts'}
                </button>
              </div>

              {expandedProgramId === program.id && programDetail && (
                <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-6">
                  {/* Fees */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800 inline-flex items-center gap-1">
                        <CurrencyDollarIcon className="h-5 w-5" /> Program Fees
                      </h4>
                      <button
                        onClick={() => { setShowFeeForm(v => !v); setFeeForm(emptyFeeForm); }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {showFeeForm ? 'Cancel' : '+ Add fee'}
                      </button>
                    </div>

                    {programDetail.fees.length === 0 ? (
                      <p className="text-sm text-gray-500">No fees configured. Add a fee that will be invoiced when players are assigned.</p>
                    ) : (
                      <div className="space-y-2">
                        {programDetail.fees.map(fee => (
                          <div key={fee.id} className="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-2">
                            <div className="text-sm">
                              <span className="font-medium text-gray-900">{fee.title}</span>
                              <span className="text-gray-700"> - ${Number(fee.amount).toFixed(2)}</span>
                              {fee.age_group_id && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                                  {ageGroupName(fee.age_group_id) || fee.AgeGroup?.name || `Age group #${fee.age_group_id}`}
                                </span>
                              )}
                              {fee.team_id && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded">
                                  {fee.Team?.name || `Team #${fee.team_id}`}
                                </span>
                              )}
                              {!fee.age_group_id && !fee.team_id && (
                                <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Program-wide</span>
                              )}
                              {fee.payment_plan_enabled && (
                                <span className="ml-2 text-xs text-gray-500">payment plan</span>
                              )}
                            </div>
                            <button onClick={() => archiveFee(fee.id, program.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Archive fee">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {showFeeForm && (
                      <form onSubmit={(e) => submitFee(e, program.id)} className="mt-3 bg-white rounded border border-gray-200 p-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
                            <input name="title" value={feeForm.title} onChange={handleFeeFieldChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Season Dues" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Amount (USD) *</label>
                            <input type="number" min="0" step="1" name="amount" value={feeForm.amount} onChange={handleFeeFieldChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="1200" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                          <input name="description" value={feeForm.description} onChange={handleFeeFieldChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Uniform, tournaments, etc." />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Scope (age group)</label>
                            <select name="age_group_id" value={feeForm.age_group_id} onChange={handleFeeFieldChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                              <option value="">Program-wide</option>
                              {ageGroups.map(ag => <option key={ag.id} value={ag.id}>{ag.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Due date</label>
                            <input type="date" name="due_date" value={feeForm.due_date} onChange={handleFeeFieldChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Program end date</label>
                            <input type="date" name="program_end_date" value={feeForm.program_end_date} onChange={handleFeeFieldChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input type="checkbox" name="auto_invoice_on_assignment" checked={feeForm.auto_invoice_on_assignment} onChange={handleFeeFieldChange}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                          <label className="ml-2 text-sm text-gray-900">Auto-invoice when a player is assigned</label>
                        </div>
                        <div className="flex items-center">
                          <input type="checkbox" name="payment_plan_enabled" checked={feeForm.payment_plan_enabled} onChange={handleFeeFieldChange}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                          <label className="ml-2 text-sm text-gray-900">Offer payment plan</label>
                        </div>
                        {feeForm.payment_plan_enabled && (
                          <div className="flex flex-wrap gap-3 ml-6">
                            {PLAN_OPTION_CHOICES.map(months => (
                              <label key={months} className="flex items-center text-sm text-gray-700">
                                <input type="checkbox" checked={feeForm.payment_plan_options.includes(months)} onChange={() => togglePlanOption(months)}
                                  className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                <span className="ml-1">{months} mo</span>
                              </label>
                            ))}
                          </div>
                        )}
                        <div className="flex justify-end">
                          <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">Add fee</button>
                        </div>
                      </form>
                    )}
                  </div>

                  {/* Teams */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800 inline-flex items-center gap-1">
                        <UsersIcon className="h-5 w-5" /> Teams
                      </h4>
                      <button
                        onClick={async () => {
                          await loadOrgTeams();
                          setShowTeamPicker(v => !v);
                        }}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {showTeamPicker ? 'Cancel' : '+ Add team'}
                      </button>
                    </div>

                    {programDetail.teams.length === 0 ? (
                      <p className="text-sm text-gray-500">No teams assigned yet. Add existing teams or they'll be linked when created with this program.</p>
                    ) : (
                      <div className="space-y-2">
                        {programDetail.teams.map(t => (
                          <div key={t.id} className="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-2">
                            <div className="text-sm">
                              <span className="font-medium text-gray-900">{t.name}</span>
                              {t.ageGroup && <span className="ml-2 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{t.ageGroup}</span>}
                              {t.season && <span className="ml-1 text-gray-500 text-xs">{t.season}</span>}
                            </div>
                            <button
                              onClick={() => removeTeamFromProgram(program.id, t.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600"
                              title="Remove from program"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {showTeamPicker && (
                      <div className="mt-3 bg-white rounded border border-gray-200 p-3">
                        <p className="text-xs text-gray-500 mb-2">Select an org team to add:</p>
                        {orgTeams.filter(t => !programDetail.teams.some(pt => pt.id === t.id)).length === 0 ? (
                          <p className="text-sm text-gray-500">All org teams are already in this program.</p>
                        ) : (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {orgTeams
                              .filter(t => !programDetail.teams.some(pt => pt.id === t.id))
                              .map(t => (
                                <button
                                  key={t.id}
                                  onClick={() => addTeamToProgram(program.id, t.id)}
                                  className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-50 flex items-center justify-between"
                                >
                                  <span className="font-medium text-gray-900">{t.name}</span>
                                  <span className="text-xs text-gray-500">{t.ageGroup} {t.season && `· ${t.season}`}</span>
                                </button>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tryouts */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Tryouts in this program</h4>
                    {programDetail.tryouts.length === 0 ? (
                      <p className="text-sm text-gray-500">No tryouts associated yet. Create tryouts from the Calendar and select this program.</p>
                    ) : (
                      <div className="space-y-2">
                        {programDetail.tryouts.map(t => (
                          <div key={t.id} className="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-2 text-sm">
                            <span className="text-gray-900">{t.title} <span className="text-gray-500">- {t.date}</span></span>
                            <button
                              onClick={() => navigate(`/tryout-assignment?eventId=${t.id}`)}
                              className="text-primary-600 hover:text-primary-700"
                            >
                              Open tryout
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Program create/edit modal */}
      {showProgramForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingProgram ? 'Edit Program' : 'New Program'}</h2>
            </div>
            <form onSubmit={submitProgram} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input name="name" value={programForm.name} onChange={handleProgramFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Fall 2026" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" value={programForm.description} onChange={handleProgramFieldChange} rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start date *</label>
                  <input type="date" name="start_date" value={programForm.start_date} onChange={handleProgramFieldChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  <p className="text-xs text-gray-500 mt-1">Drives player playing-age for this program.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                  <input type="date" name="end_date" value={programForm.end_date} onChange={handleProgramFieldChange}
                    min={programForm.start_date || undefined}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Season label</label>
                <input name="season" value={programForm.season} onChange={handleProgramFieldChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Fall 2026" />
              </div>

              <div className="pt-3 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Season structure (rec leagues)</h3>
                <p className="text-xs text-gray-500 mb-3">Optional — set these to auto-generate practices, round-robin games, and a seeded playoff bracket for this program.</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pre-season start (draft day)</label>
                    <input type="date" name="preseason_start_date" value={programForm.preseason_start_date} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Regular season start (opening day)</label>
                    <input type="date" name="regular_season_start_date" value={programForm.regular_season_start_date} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Regular season end</label>
                    <input type="date" name="regular_season_end_date" value={programForm.regular_season_end_date} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Postseason start</label>
                    <input type="date" name="postseason_start_date" value={programForm.postseason_start_date} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Postseason format</label>
                    <select name="postseason_format" value={programForm.postseason_format} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      {POSTSEASON_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Round-robin: times each team plays each other</label>
                    <input type="number" min="1" max="4" name="round_robin_times_through" value={programForm.round_robin_times_through} onChange={handleProgramFieldChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowProgramForm(false); setEditingProgram(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  {editingProgram ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;
